import { useMemo } from 'react'
import { useMealNeeds } from '@/modules/meals/hooks/useMealNeeds'
import { useGearNeeds } from '@/modules/gear/hooks/useGearNeeds'

export type ChecklistItemId = 'radio' | 'details' | 'itinerary' | 'meals' | 'gear' | 'crew'

export interface ChecklistItem {
  id: ChecklistItemId
  label: string
  completed: boolean
}

export interface TripChecklistTrip {
  radio_channel?: string | null
  start_date?: string | null
  end_date?: string | null
  description?: string | null
}

interface TripChecklistCounts {
  waypointCount: number
  memberCount: number
  mealCount: number
  gearCount: number
}

/** What a trip needs before it's ready. Trip Readiness and Next Steps both read from this, so they can't disagree. */
export function buildTripChecklist(trip: TripChecklistTrip, counts: TripChecklistCounts): ChecklistItem[] {
  return [
    { id: 'radio', label: 'Radio Channel', completed: Boolean(trip.radio_channel) },
    { id: 'details', label: 'Trip Details', completed: Boolean(trip.start_date && trip.end_date && trip.description) },
    { id: 'itinerary', label: 'Itinerary', completed: counts.waypointCount > 0 },
    { id: 'meals', label: 'Meal Plan', completed: counts.mealCount > 0 },
    { id: 'gear', label: 'Gear Checklist', completed: counts.gearCount > 0 },
    { id: 'crew', label: 'Invite Crew', completed: counts.memberCount > 1 }
  ]
}

export function useTripChecklist(
  tripId: string,
  trip: TripChecklistTrip,
  { waypointCount, memberCount }: Pick<TripChecklistCounts, 'waypointCount' | 'memberCount'>
) {
  const { meals, isLoading: isLoadingMeals } = useMealNeeds(tripId, { skip: false })
  const { gearNeeds, isLoading: isLoadingGear } = useGearNeeds(tripId)
  const mealCount = meals?.length ?? 0
  const gearCount = gearNeeds?.length ?? 0

  const checklist = useMemo(
    () => buildTripChecklist(trip, { waypointCount, memberCount, mealCount, gearCount }),
    [trip, waypointCount, memberCount, mealCount, gearCount]
  )

  return { checklist, isLoading: isLoadingMeals || isLoadingGear }
}
